import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Modal, Radio, message } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import { fetchBatchChooseCoupon } from '@/api/guest';
import { CouponType, DATE_FORMAT, OccType } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import EditCellTable, { IcustomColumnType } from '../EditCellTable';

// 选券方式
export enum CouponMatchMethod {
  // 最优惠
  PreSale = 1,
  // 先过期
  PreDue = 2,
}

const columns: Array<IcustomColumnType<any>> = [
  { title: intl.get('app_constants_room_type'), dataIndex: 'roomTypeName' },
  {
    title: intl.get('app_constants_room_no'),
    dataIndex: 'roomNo',
    render: (val) => val || intl.get('app_reservation_room_queue_no'),
  },
];

type RoomInfoItem = {
  virtualRoomNo: string;
  roomId: number;
  roomNo: string;
  roomTypeId: number;
  roomTypeName: string;
};

type CouponItem = {
  couponAmount: number;
  couponName: string;
  couponNo: string;
  couponSubType: string;
  couponType: string;
  couponWyuid: string;
};

type modalParams = {
  extra: {
    wyuid: string;
    resvNo: string;
    // 换房时传
    couponNos?: string;
  } & { [key: string]: any };
  dateStrRange: Array<string>;
  priceRooms: Array<
    RoomInfoItem & {
      recNo: string;
      datePrices: Array<
        CouponItem & {
          key: string;
          id: number;
          occType: OccType;
          rentDate: string;
          roomPrice: number;
        }
      >;
    }
  >;
  onConfirm?: (...args) => void;
  onReset?: (...args) => void;
};

export interface IRefProps {
  openModal?: (value: modalParams) => void;
}

const BatchCouponModal = React.forwardRef<IRefProps, any>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);

  const [limitDateRange, setLimitDateRange] = useState<Array<moment.Moment>>([]);

  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<Array<any>>([]);
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [calcLoading, setCalcLoading] = useState<boolean>(false);

  const paramsRef = useRef<Pick<modalParams, 'extra' | 'onReset' | 'onConfirm'>>();

  // 同步更新state和表单值
  const syncUpdateRoomTypeValue = (valueList) => {
    setSelectedRow(valueList);
    form.setFieldsValue({ chosenRooms: valueList });
  };

  const handleCheckBoxchange = (_, selectedRows) => {
    syncUpdateRoomTypeValue(selectedRows);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const initCouponList = async (data) => {
    try {
      const res = await fetchBatchChooseCoupon(data);
      if (res.code === 0) {
        return res.data?.priceRooms || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const formateDateStr = (rangeDate) => {
    return Array.isArray(rangeDate) && rangeDate.length >= 2
      ? [
          getTimeByUtcOffset(rangeDate[0], DATE_FORMAT),
          getTimeByUtcOffset(rangeDate[1], DATE_FORMAT),
        ]
      : [];
  };
  // 根据日期过滤指定间夜
  const filterRoomsByDateRangeAndId = (virtualRoomNoList, dateStrList) => {
    return dataSource
      ?.filter((item) => virtualRoomNoList?.includes(item.virtualRoomNo))
      ?.map((item) => ({
        ...item,
        datePrices: item.datePrices?.filter(
          (subItem) => subItem.rentDate >= dateStrList[0] && subItem.rentDate <= dateStrList[1],
        ),
      }))
      ?.filter((item) => item.datePrices?.length);
  };

  // 一键取消
  const handleResetChoose = async () => {
    const { rangeDate, chosenRooms } = await form.getFieldsValue();
    if (!rangeDate || rangeDate?.length < 2 || !chosenRooms || !chosenRooms.length) {
      message.error(intl.get('cpm_batch_coupon_modal_date_range_not_select'));
      return;
    }
    const { extra, onReset } = paramsRef.current || {};
    const dateStrRange = formateDateStr(rangeDate);
    const virtualRoomNoList = chosenRooms?.map((item) => item.virtualRoomNo);
    if (onReset) {
      onReset({
        extra,
        dateStrRange,
        priceRooms: filterRoomsByDateRangeAndId(virtualRoomNoList, dateStrRange)?.map((item) => ({
          ...item,
          datePrices: item.datePrices?.map((priceItem) => ({
            ...priceItem,
            couponNo: null,
            couponAmount: null,
            couponName: null,
            couponSubType: null,
            couponType: null,
            couponWyuid: null,
          })),
        })),
      });
    }
    setOpen(false);
  };

  const getRequestParams = (data) => {
    const { rangeDate, chosenRooms, ...rest } = data || {};
    const result = { ...rest };
    result.startDate = rangeDate?.[0] ? moment(rangeDate?.[0]).valueOf() : undefined;
    result.endDate = rangeDate?.[1] ? moment(rangeDate?.[1]).valueOf() : undefined;
    result.chosenRooms = chosenRooms?.map((item) => ({
      roomId: item.roomId,
      roomNo: item.roomNo,
      roomTypeId: item.roomTypeId,
      roomTypeName: item.roomTypeName,
      virtualRoomNo: item.virtualRoomNo,
    }));
    return result;
  };

  // 一键选券
  const handleChoose = async () => {
    const values = await form.validateFields();
    // 校验间夜是否超过100
    const totalDays = values?.rangeDate[1].diff(values?.rangeDate[0], 'day');
    if ((totalDays + 1) * selectedRow?.length > 100) {
      Modal.warning({
        title: intl.get('cpm_batch_coupon_modal_exceed_one_key_select_limit_tips1'),
        content: intl.get('cpm_batch_coupon_modal_exceed_one_key_select_limit_tips2'),
      });
      return;
    }
    const dateStrRange = formateDateStr(values.rangeDate);
    const { extra = { wyuid: '', resvNo: '', couponNos: [] }, onConfirm } = paramsRef.current || {};
    const params = getRequestParams(values);
    params.wyuid = extra.wyuid;
    params.resvNo = extra.resvNo;
    params.couponNos = extra.couponNos;
    params.priceRooms = dataSource;
    try {
      setCalcLoading(true);
      const result = await initCouponList(params);
      setCalcLoading(false);
      if (onConfirm) {
        onConfirm({ extra, dateStrRange, priceRooms: result });
      }
    } catch (error) {
      setCalcLoading(false);
    }
    setOpen(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      openModal(params) {
        // 优惠券类型、选券方式默认值
        form.setFieldsValue({
          couponTypes: [CouponType.Deduction, CouponType.Discount],
          couponMatchMethod: CouponMatchMethod.PreDue,
        });

        const { extra, dateStrRange, priceRooms, onConfirm, onReset } = params || {};
        paramsRef.current = { extra, onConfirm, onReset };
        //  日期默认值
        let rangeDate = [];
        if (Array.isArray(dateStrRange) && dateStrRange?.length >= 2) {
          rangeDate = [moment(dateStrRange?.[0]), moment(dateStrRange?.[1])];
        }
        form.setFieldsValue({ rangeDate });
        setLimitDateRange(rangeDate);

        const list = (priceRooms || [])?.map((item, index) => ({
          ...item,
          id: index,
          virtualRoomNo: index,
        }));
        setDataSource(list);
        // 房型默认值
        syncUpdateRoomTypeValue(list);
        setOpen(true);
      },
    }),
    [],
  );

  const selectedRowKeys = selectedRow?.map((item) => item.id);

  return (
    <>
      <Modal
        width={700}
        title={intl.get('cpm_batch_coupon_modal_one_key_select')}
        destroyOnClose
        maskClosable={false}
        open={open}
        onCancel={handleCancel}
        footer={
          <>
            <Button type="default" onClick={handleCancel}>
              {intl.get('app_confirm_cancel_btn')}
            </Button>
            <Button type="dashed" onClick={handleResetChoose}>
              {intl.get('cpm_batch_coupon_modal_one_key_cancel_select')}
            </Button>
            <Button loading={calcLoading} type="primary" onClick={handleChoose}>
              {intl.get('cpm_batch_coupon_modal_one_key_select')}
            </Button>
          </>
        }
      >
        <Form form={form} labelCol={{ span: 5.5 }} labelAlign="left">
          <Form.Item
            label={intl.get('cpm_batch_coupon_modal_coupon_type')}
            name="couponTypes"
            rules={[{ required: true }]}
          >
            <Checkbox.Group>
              <Checkbox value={CouponType.Deduction}>
                {intl.get('cpm_batch_coupon_modal_room_fee_deduction_voucher')}
              </Checkbox>
              <Checkbox value={CouponType.Discount}>
                {intl.get('cpm_batch_coupon_modal_room_discount_coupon')}
              </Checkbox>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            style={{ wordBreak: 'break-word' }}
            label={intl.get('cpm_batch_coupon_modal_select_coupon_way')}
            name="couponMatchMethod"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={CouponMatchMethod.PreDue}>
                {intl.get('cpm_batch_coupon_modal_expire_first_use_first')}
              </Radio>
              <Radio value={CouponMatchMethod.PreSale}>
                {intl.get('cpm_batch_coupon_modal_most_favorable_offer')}
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={intl.get('cpm_batch_coupon_modal_date_range')}
            name="rangeDate"
            rules={[{ required: true }]}
          >
            <DatePicker.RangePicker
              disabledDate={(currentDate) =>
                currentDate < limitDateRange?.[0] || currentDate > limitDateRange?.[1]
              }
            />
          </Form.Item>
          <Form.Item
            label={intl.get('cpm_batch_coupon_modal_choose_room')}
            name="chosenRooms"
            rules={[
              {
                validator() {
                  if (Array.isArray(selectedRow) && selectedRow.length) {
                    return Promise.resolve('');
                  }
                  return Promise.reject(
                    new Error(intl.get('cpm_batch_coupon_modal_select_choose_room')),
                  );
                },
              },
            ]}
          >
            <EditCellTable
              rowKey="id"
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              showRowSelection
              rowSelectionProps={{ selectedRowKeys }}
              onCheckBoxchange={handleCheckBoxchange}
              scroll={{ x: 'max-content', y: 840 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
});

export default BatchCouponModal;
